// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTable, Column } from 'react-table';
import { useEffect, useState, useRef } from 'react';
import TableLoading from 'components/atoms/TableLoading';
import styles from './ResponsiveHistoryTable.module.scss';
import { mergeClasses, objectFetch } from 'util/commons';

export type CustomColumn<T extends Record<string, any>> = Column<T> & {
  className?: string;
};

export interface ResponsiveHistoryTableProps<T extends Record<string, any>> {
  data: T[];
  columns: CustomColumn<T>[];
  className?: string;
  noDataMessage?: string;
  onLoadMore?: () => void; // When provided, automatically calculates optimal height
  scrollResetDependencies?: any[]; // When provided, scrolls to top when any dependency changes
  rowClassName?: string | ((row: T) => string); // Optional class name for rows, can be a string or a function that returns a string based on the row data
}

export function ResponsiveHistoryTable<T extends Record<string, any>>({
  data,
  columns,
  className = '',
  noDataMessage = '',
  onLoadMore,
  scrollResetDependencies = [],
  rowClassName,
}: ResponsiveHistoryTableProps<T>) {
  const tableInstance = useTable({ columns, data });
  const [calculatedMaxHeight, setCalculatedMaxHeight] = useState<string | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  useEffect(() => {
    if (!onLoadMore) {
      return;
    }

    const calculateTableHeight = () => {
      try {
        if (!containerRef.current) {
          setCalculatedMaxHeight(null);
          return;
        }

        const viewportHeight = window.innerHeight;
        const containerRect = containerRef.current.getBoundingClientRect();

        const spaceFromContainerToBottom = viewportHeight - containerRect.top;

        const containerStyles = window.getComputedStyle(containerRef.current);
        const marginBottom = parseInt(containerStyles.marginBottom) || 0;
        const paddingTop = parseInt(containerStyles.paddingTop) || 0;
        const paddingBottom = parseInt(containerStyles.paddingBottom) || 0;

        const reservedSpace = marginBottom + paddingTop + paddingBottom;

        const availableHeight = Math.max(
          200,
          spaceFromContainerToBottom - reservedSpace
        );
        setCalculatedMaxHeight(`${availableHeight}px`);
      } catch (error) {
        // use default max height
        setCalculatedMaxHeight(null);
      }
    };

    calculateTableHeight();

    const handleResize = () => calculateTableHeight();
    window.addEventListener('resize', handleResize);

    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [onLoadMore]);

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 });
  }, [...scrollResetDependencies]);

  if (!data.length) {
    return <div className={styles.noData}>{noDataMessage}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.tableContainer} ${className}`}
      style={
        onLoadMore && calculatedMaxHeight
          ? { maxHeight: calculatedMaxHeight }
          : undefined
      }
    >
      <table {...getTableProps()} className={styles.table}>
        <thead className={styles.tableHead}>
          {headerGroups.map((headerGroup, index) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, colIndex) => (
                <th
                  {...column.getHeaderProps()}
                  key={colIndex}
                  className={styles.tableHeader}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);

            return (
              <tr
                {...row.getRowProps()}
                key={index}
                className={mergeClasses(
                  styles.tableRow,
                  rowClassName instanceof Function
                    ? rowClassName(row.original)
                    : rowClassName
                )}
              >
                {row.cells.map((cell, cellIndex) => {
                  const dataLabel =
                    headerGroups[0].headers[cellIndex]?.render('Header');

                  return (
                    <td
                      {...cell.getCellProps()}
                      key={cellIndex}
                      className={mergeClasses(
                        styles.tableData,
                        objectFetch(cell.column, 'className')
                      )}
                      data-label={
                        typeof dataLabel === 'string' ? dataLabel : ''
                      }
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {onLoadMore && <TableLoading onNext={onLoadMore} />}
    </div>
  );
}

export default ResponsiveHistoryTable;
